import {
  EnvironmentProviders,
  makeEnvironmentProviders,
  OutputEmitterRef,
  Type,
} from '@angular/core';

import type { ZodType } from 'zod';

import {
  InterruptRenderer,
  provideTellentInterruptRenderer,
} from '@core/tellent-assistant-contracts';

import { TellentAssistantApprovalInterruptComponent } from './approval/approval-interrupt.component';
import { TellentAssistantChoiceInterruptComponent } from './choice/choice-interrupt.component';
import {
  approvalPayloadSchema,
  choicePayloadSchema,
  textInputPayloadSchema,
  toolApprovalPayloadSchema,
  type ApprovalPayload,
  type ApprovalResume,
  type ChoicePayload,
  type ChoiceResume,
  type TextInputPayload,
  type TextInputResume,
  type ToolApprovalPayload,
} from './schemas';
import { TellentAssistantTextInputInterruptComponent } from './text-input/text-input-interrupt.component';

type TellentInterruptRendererComponent<P, R> = Type<{
  payload: P;
  resolve: OutputEmitterRef<R>;
  cancel?: OutputEmitterRef<void>;
}>;

function createInterruptRendererProvider<P, R>(
  reason: string,
  schema: ZodType<P>,
  component: Type<unknown>,
): EnvironmentProviders {
  const renderer: InterruptRenderer<P, R> = {
    reason,
    schema,
    component: <TellentInterruptRendererComponent<P, R>>component,
  };
  return makeEnvironmentProviders([provideTellentInterruptRenderer({ useValue: renderer })]);
}

export function provideTellentApprovalInterruptRenderer(): EnvironmentProviders {
  return createInterruptRendererProvider<ApprovalPayload, ApprovalResume>(
    'approval',
    approvalPayloadSchema,
    TellentAssistantApprovalInterruptComponent,
  );
}

export function provideTellentChoiceInterruptRenderer(): EnvironmentProviders {
  return createInterruptRendererProvider<ChoicePayload, ChoiceResume>(
    'choice',
    choicePayloadSchema,
    TellentAssistantChoiceInterruptComponent,
  );
}

export function provideTellentTextInputInterruptRenderer(): EnvironmentProviders {
  return createInterruptRendererProvider<TextInputPayload, TextInputResume>(
    'text_input',
    textInputPayloadSchema,
    TellentAssistantTextInputInterruptComponent,
  );
}

export function provideTellentToolApprovalInterruptRenderer(): EnvironmentProviders {
  return createInterruptRendererProvider<ToolApprovalPayload, ApprovalResume>(
    'tool_approval',
    toolApprovalPayloadSchema,
    TellentAssistantApprovalInterruptComponent,
  );
}
